<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import ContactSortMenu from './components/ContactSortMenu.vue';
import ContactMoreActions from './components/ContactMoreActions.vue';
import ContactSegmentsMenu from './components/ContactSegmentsMenu.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';

const props = defineProps({
  showSearch: { type: Boolean, default: true },
  searchValue: { type: String, default: '' },
  headerTitle: { type: String, required: true },
  buttonLabel: { type: String, default: '' },
  activeSort: { type: String, default: 'last_activity_at' },
  activeOrdering: { type: String, default: '' },
  isSegmentsView: { type: Boolean, default: false },
  hasActiveFilters: { type: Boolean, default: false },
  isLabelView: { type: Boolean, default: false },
  isActiveView: { type: Boolean, default: false },
});

const emit = defineEmits([
  'search',
  'filter',
  'update:sort',
  'add',
  'import',
  'export',
  'createSegment',
  'deleteSegment',
]);

const { t } = useI18n();
// SiteUp: no segmento o lápis edita o segmento; a dica diz isso.
const filterButtonLabel = computed(() =>
  props.isSegmentsView
    ? t('CONTACTS_LAYOUT.FILTER.EDIT_SEGMENT')
    : t('CONTACTS_LAYOUT.HEADER.ACTIONS.FILTER_LABEL')
);
</script>

<template>
  <header class="sticky top-0 z-20 px-6">
    <div
      class="flex flex-col sm:flex-row sm:items-center justify-between w-full py-6 gap-3 sm:gap-2 mx-auto max-w-5xl"
    >
      <span class="text-xl font-medium truncate text-n-slate-12">
        {{ headerTitle }}
      </span>
      <div
        class="flex items-center flex-col sm:flex-row sm:flex-shrink-0 w-full sm:w-auto gap-3 sm:gap-4"
      >
        <div v-if="showSearch" class="flex items-center gap-2 w-full">
          <Input
            :model-value="searchValue"
            type="search"
            :aria-label="$t('CONTACTS_LAYOUT.HEADER.SEARCH_TITLE')"
            :placeholder="$t('CONTACTS_LAYOUT.HEADER.SEARCH_PLACEHOLDER')"
            :custom-input-class="[
              '!h-11 !rounded-xl [&:not(.focus)]:!border-transparent bg-n-alpha-2 dark:bg-n-solid-1 ltr:!pl-8 !py-1 rtl:!pr-8',
            ]"
            class="w-full"
            @input="emit('search', $event.target.value)"
          >
            <template #prefix>
              <Icon
                icon="i-ph-magnifying-glass"
                class="absolute -translate-y-1/2 text-n-slate-11 size-4 top-1/2 ltr:left-2 rtl:right-2"
              />
            </template>
          </Input>
        </div>
        <div
          class="flex flex-wrap items-center flex-shrink-0 w-full sm:w-auto justify-between sm:justify-start gap-4"
        >
          <div class="flex items-center gap-2">
            <div v-if="!isLabelView && !isActiveView" class="relative">
              <Button
                id="toggleContactsFilterButton"
                v-tooltip.top="filterButtonLabel"
                :icon="isSegmentsView ? 'i-ph-pencil-simple' : 'i-ph-funnel'"
                color="slate"
                size="sm"
                class="relative !h-11 !w-11 !rounded-xl"
                :aria-label="filterButtonLabel"
                variant="ghost"
                @click="emit('filter')"
              >
                <div
                  v-if="hasActiveFilters && !isSegmentsView"
                  class="absolute top-0 right-0 w-2 h-2 rounded-full bg-n-brand"
                />
              </Button>
              <slot name="filter" />
            </div>
            <Button
              v-if="
                hasActiveFilters &&
                !isSegmentsView &&
                !isLabelView &&
                !isActiveView
              "
              :label="
                $t(
                  'CONTACTS_LAYOUT.HEADER.ACTIONS.FILTERS.CREATE_SEGMENT.CONFIRM'
                )
              "
              icon="i-ph-floppy-disk"
              class="!h-11 !rounded-xl"
              color="slate"
              size="sm"
              variant="faded"
              data-testid="contact-save-segment"
              @click="emit('createSegment')"
            />
            <Button
              v-if="isSegmentsView && !isLabelView && !isActiveView"
              v-tooltip.top="
                $t('CONTACTS_LAYOUT.HEADER.ACTIONS.FILTERS.DELETE_SEGMENT.BUTTON')
              "
              :aria-label="
                $t(
                  'CONTACTS_LAYOUT.HEADER.ACTIONS.FILTERS.DELETE_SEGMENT.BUTTON'
                )
              "
              icon="i-ph-trash"
              class="!h-11 !w-11 !rounded-xl"
              color="slate"
              size="sm"
              variant="ghost"
              @click="emit('deleteSegment')"
            />
            <ContactSegmentsMenu />
            <ContactSortMenu
              :active-sort="activeSort"
              :active-ordering="activeOrdering"
              @update:sort="emit('update:sort', $event)"
            />
            <ContactMoreActions
              @add="emit('add')"
              @import="emit('import')"
              @export="emit('export')"
            />
          </div>
          <div class="w-px h-4 bg-n-strong" />
          <ComposeConversation>
            <template #trigger>
              <Button
                :label="buttonLabel"
                size="sm"
                class="!h-11 !rounded-xl"
              />
            </template>
          </ComposeConversation>
        </div>
      </div>
    </div>
  </header>
</template>
